import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, IndianRupee, Calendar, User, Phone } from 'lucide-react';
import Layout from '@/components/Layout';

export default function PendingRentsPage() {
  const { pgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingRents();
  }, [pgId]);

  const fetchPendingRents = async () => {
    try {
      const response = await api.getPendingRents(pgId);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch pending rents');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600">Failed to load pending rents</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <Button
            onClick={() => navigate('/pgs')}
            variant="ghost"
            className="mb-4"
            data-testid="back-to-pgs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to PG List
          </Button>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="break-words text-2xl font-bold text-slate-900 font-heading sm:text-3xl">{data.pgName}</h1>
              <p className="text-slate-600 mt-1">Pending Rent Collection</p>
              <p className="text-sm text-slate-500 mt-1">
                Current Month: {getMonthName(data.currentMonth)}
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-white rounded-xl border-2 border-slate-200 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 shrink-0 bg-red-100 rounded-lg flex items-center justify-center sm:h-12 sm:w-12">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-700">Tenants with Dues</p>
                <p className="text-2xl font-bold text-slate-900 sm:text-3xl">{data.pendingCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <IndianRupee className="h-8 w-8 shrink-0 opacity-80 sm:h-10 sm:w-10" />
              <div className="min-w-0">
                <p className="text-sm opacity-90">Total Pending Amount</p>
                <p className="break-all text-2xl font-bold sm:text-3xl">₹{data.totalPendingAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-4 sm:p-6 shadow-sm">
            <Button
              onClick={() => navigate('/collect-rent')}
              className="w-full h-14 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid="collect-rent-button"
            >
              <IndianRupee className="w-5 h-5 mr-2" />
              Collect Rent Now
            </Button>
          </div>
        </div>

        {/* Pending Rents List */}
        {data.pendingRents.length === 0 ? (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-900 mb-2">All Caught Up! 🎉</h3>
            <p className="text-green-700">No pending rents. All tenants have paid for this month.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.pendingRents.map((tenant) => (
              <div
                key={tenant.tenantId}
                className="bg-white rounded-xl border-2 border-red-200 overflow-hidden shadow-sm"
                data-testid={`pending-tenant-${tenant.tenantId}`}
              >
                <div className="bg-red-50 border-b border-red-200 p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="h-11 w-11 shrink-0 bg-red-100 rounded-full flex items-center justify-center sm:h-12 sm:w-12">
                          <User className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="break-words text-lg font-bold text-slate-900">{tenant.tenantName}</h3>
                          <div className="mt-2 flex flex-col gap-2 text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                            <span className="flex min-w-0 items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span className="truncate">{tenant.tenantPhone}</span>
                            </span>
                            <span className="flex min-w-0 items-start gap-1">
                              <Calendar className="mt-0.5 h-4 w-4 shrink-0" />
                              <span className="break-words">
                                Joined: {new Date(tenant.joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg bg-white/80 px-3 py-2 text-left sm:text-right">
                      <p className="text-sm font-medium text-slate-600 mb-1">Total Due</p>
                      <p className="break-all text-2xl font-bold text-red-600">₹{tenant.totalDue.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Pending Months:</p>
                  <div className="space-y-2">
                    {tenant.dues.map((due, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                          <span className="font-medium text-slate-900">{getMonthName(due.month)}</span>
                          {index === 0 && tenant.dues.length > 1 && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                              Oldest
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          <span className="text-lg font-bold text-slate-900">₹{due.amount.toLocaleString('en-IN')}</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                            {due.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Note:</span> Monthly Rent: ₹{tenant.monthlyRent.toLocaleString('en-IN')}
                      {tenant.dues.length > 1 && ' • Multiple months pending'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Footer */}
        {data.pendingRents.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  ₹{data.totalPendingAmount.toLocaleString('en-IN')} Total Due
                </h3>
                <p className="text-sm text-slate-600">
                  Collect rent from {data.pendingCount} tenant{data.pendingCount > 1 ? 's' : ''} to clear all dues
                </p>
              </div>
              <Button
                onClick={() => navigate('/collect-rent')}
                className="h-14 w-full px-8 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg sm:w-auto"
              >
                <IndianRupee className="w-5 h-5 mr-2" />
                Collect Rent
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
